import { useEffect, useState } from 'react';
import axios from 'axios';

interface LearningPattern {
  id: string;
  patternType: string;
  description: string;
  confidence: number;
  occurrences: number;
  lastSeen: string;
  impact: 'high' | 'medium' | 'low';
}

interface LearningInsightsProps {
  compact?: boolean;
}

export default function LearningInsights({ compact = false }: LearningInsightsProps) {
  const [patterns, setPatterns] = useState<LearningPattern[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPattern, setSelectedPattern] = useState<LearningPattern | null>(null);

  useEffect(() => {
    loadPatterns();
    const interval = setInterval(loadPatterns, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadPatterns = async () => {
    try {
      const response = await axios.get('/api/v1/telemetry/learning-patterns', {
        params: {
          limit: compact ? 5 : 15,
        },
      });
      setPatterns(response.data.patterns || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load learning patterns:', error);
      // Show empty state when API is unavailable
      setPatterns([]);
      setLoading(false);
    }
  };

  const impactColors = {
    high: 'bg-purple-900/20 text-purple-400 border-purple-500/30',
    medium: 'bg-blue-900/20 text-blue-400 border-blue-500/30',
    low: 'bg-gray-800/50 text-gray-400 border-gray-700',
  };

  const impactIcons = {
    high: '🔥',
    medium: '⚡',
    low: '💡',
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-green-400';
    if (confidence >= 0.8) return 'text-blue-400';
    if (confidence >= 0.7) return 'text-yellow-400';
    return 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-400">Loading learning insights...</div>
      </div>
    );
  }

  const highImpactCount = patterns.filter((p) => p.impact === 'high').length;
  const avgConfidence = patterns.reduce((sum, p) => sum + p.confidence, 0) / patterns.length;
  const totalOccurrences = patterns.reduce((sum, p) => sum + p.occurrences, 0);

  return (
    <div className={`flex flex-col h-full ${compact ? 'p-4' : 'space-y-4'}`}>
      {!compact && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Learning Insights</h2>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🧠</span>
                <span className="text-sm text-purple-400 font-medium">Patterns</span>
              </div>
              <div className="text-3xl font-bold text-purple-400">{patterns.length}</div>
            </div>
            <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔥</span>
                <span className="text-sm text-purple-400 font-medium">High Impact</span>
              </div>
              <div className="text-3xl font-bold text-purple-400">{highImpactCount}</div>
            </div>
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">📊</span>
                <span className="text-sm text-blue-400 font-medium">Avg Confidence</span>
              </div>
              <div className="text-3xl font-bold text-blue-400">{(avgConfidence * 100).toFixed(1)}%</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">📈</span>
                <span className="text-sm text-gray-400 font-medium">Observations</span>
              </div>
              <div className="text-3xl font-bold text-gray-100">{totalOccurrences.toLocaleString()}</div>
            </div>
          </div>

          <div className="text-sm text-gray-400">
            Machine learning models continuously analyze workflow patterns, exceptions, and performance metrics to identify actionable insights.
          </div>
        </div>
      )}

      {/* Patterns List */}
      <div className={`bg-gray-900 border border-gray-800 rounded-lg overflow-hidden flex-1 ${compact ? '' : 'min-h-96'}`}>
        <div className="overflow-auto h-full">
          {patterns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
              <div className="text-4xl mb-4">🧠</div>
              <div className="text-lg font-medium mb-2">No patterns learned yet</div>
              <div className="text-sm">System is collecting data...</div>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {patterns.map((pattern) => (
                <div
                  key={pattern.id}
                  onClick={() => setSelectedPattern(pattern)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    impactColors[pattern.impact]
                  } ${selectedPattern?.id === pattern.id ? 'ring-2 ring-purple-500' : 'hover:border-gray-600'}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-lg">{impactIcons[pattern.impact]}</span>
                        <span className="font-medium">{pattern.patternType}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          impactColors[pattern.impact]
                        }`}>
                          {pattern.impact} impact
                        </span>
                        {!compact && (
                          <span className="text-xs text-gray-500">
                            {pattern.occurrences} occurrences
                          </span>
                        )}
                      </div>

                      <div className="text-sm mb-3 leading-relaxed">
                        {pattern.description}
                      </div>

                      {!compact && (
                        <div className="flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">Confidence:</span>
                            <span className={`font-medium ${getConfidenceColor(pattern.confidence)}`}>
                              {(pattern.confidence * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400">Last seen:</span>
                            <span className="text-gray-300">
                              {new Date(pattern.lastSeen).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Confidence meter */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-12 h-12 rounded-full border-4 border-gray-700 flex items-center justify-center">
                        <span className={`text-sm font-bold ${getConfidenceColor(pattern.confidence)}`}>
                          {Math.round(pattern.confidence * 100)}
                        </span>
                      </div>
                      {!compact && (
                        <span className="text-xs text-gray-500">Score</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!compact && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-400">
              Showing {patterns.length} learned patterns
            </span>
            <span className="text-gray-500">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
